import { Form, Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import RadioCards from 'components/RadioCards/RadioCards';

const CARE_NEED_TYPE_OPTIONS = [
  { value: 'BEFORE_SCHOOL', label: 'Before school care' },
  { value: 'AFTER_SCHOOL', label: 'After school care' },
  {
    value: 'BEFORE_AND_AFTER_SCHOOL',
    label: 'Before and after school care',
  },
  { value: 'SUMMER', label: 'Summer care' },
];

export function FamilyApplicationChildrenCareNeedType({
  application,
  setApplication,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  // Pre-populate from existing schedule on the child object
  useEffect(() => {
    const existing = currentChild?.schedules?.[0];
    if (existing?.careNeedType) {
      form.setFieldsValue({ careNeedType: existing.careNeedType?.code });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({ applicationId: _newApplication.id, newStep: nextStep });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const existingSchedule = currentChild?.schedules?.[0];
        const isChangingType =
          existingSchedule?.careNeedType?.code && existingSchedule.careNeedType.code !== values.careNeedType;

        const savedSchedule = await familyService.saveChildSchedule(
          application.id,
          currentChild.id,
          existingSchedule?.id || null,
          {
            ...(existingSchedule || {}),
            careNeedType: { code: values.careNeedType },
            ...(isChangingType ? { days: [] } : {}),
          },
        );

        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = {
          ...currentChild,
          schedules: [savedSchedule, ...(currentChild.schedules || []).filter((s) => s.id !== savedSchedule.id)],
        };

        const updatedApplication = { ...application, children: updatedChildren };
        const finalApplication = await familyService.saveApplication(updatedApplication);
        setApplication(finalApplication);

        _goToNextStep(finalApplication);
      } catch (error) {
        console.error('Error saving care need type:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, readOnly, setApplication, setStep, stepConfig],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Care Need Type`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner
              question={`What's ${childFirstName}'s most immediate care need?`}
              guidance="Select one option"
              className="my-12"
            />

            <Form.Item name="careNeedType" rules={[{ required: true, message: 'Please select a care need type' }]}>
              <RadioCards options={CARE_NEED_TYPE_OPTIONS} data-testid="care-need-type" />
            </Form.Item>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_CARE_NEED_TYPE,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({ applicationId: application?.id, newStep: previousStep });
            },
            disabled: submitting,
            'data-testid': 'children-care-need-type-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-care-need-type-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
